import { Check, Info, PencilSimpleLine, X } from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useState } from "react";

import { Button } from "common/components/ui/Button";
import Progress from "common/components/ui/Progress";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import WorkspaceImage from "common/components/WorkspaceImage";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import type { Workspace } from "common/types";

import SeatCountSpinner from "./SeatCountSpinner";

function WorkspacesTableRow({
  workspace,
  isEditing,
  toggleIsEditing,
}: {
  workspace: Workspace;
  isEditing: boolean;
  toggleIsEditing: () => void;
}) {
  const {
    id,
    agency_license_count,
    lifetime_license_count,
    name,
    icon_url,
    license_count,
    subscription_status,
  } = workspace;
  const [seatCount, setSeatCount] = useState(agency_license_count);

  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();

  const { agencyLicensedAccounts } = useLicensedAccounts(id);

  const hasOutreachLicenses =
    !!(license_count - agency_license_count - lifetime_license_count) ||
    (subscription_status && subscription_status !== "cancelled");

  async function onSubmit() {
    if (seatCount !== agency_license_count) {
      await setLifetimeLicenseCount({
        workspaceId: id,
        count: seatCount,
        source: "agency",
        accountsToCancel: [],
      });
    }
    toggleIsEditing();
  }

  let actionComponent = (
    <Button
      variant="ghost-black"
      size="sm"
      intent="iconOnly"
      onClick={toggleIsEditing}
    >
      <PencilSimpleLine />
    </Button>
  );

  if (hasOutreachLicenses) {
    actionComponent = (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost-black" size="sm" intent="iconOnly">
            <Info />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="text-center">
          Cannot assign Agency seats to an Outreach plan workspace
        </TooltipContent>
      </Tooltip>
    );
  } else if (isEditing) {
    actionComponent = (
      <div className="flex gap-x-2 justify-end">
        <Button
          variant="secondary-black"
          size="sm"
          intent="iconOnly"
          disabled={isSettingLifetimeLicenseCount}
          onClick={() => {
            toggleIsEditing();
            //reset state on cancel
            setSeatCount(agency_license_count);
          }}
        >
          <X />
        </Button>
        <Button
          variant="secondary-purple"
          size="sm"
          intent="iconOnly"
          isLoading={isSettingLifetimeLicenseCount}
          onClick={onSubmit}
        >
          <Check />
        </Button>
      </div>
    );
  }

  return (
    <TableRow key={id}>
      <TableCell>
        <div className="flex gap-x-2.5 items-center">
          <WorkspaceImage
            id={id}
            imageSrc={icon_url}
            className={hasOutreachLicenses && "opacity-50"}
          />
          <span
            className={clsx(
              "text-md-bold",
              hasOutreachLicenses && "text-black-400",
            )}
          >
            {name}
          </span>
        </div>
      </TableCell>

      <TableCell
        className={clsx(
          "text-xs-bold",
          hasOutreachLicenses && "text-black-400",
        )}
      >
        {agencyLicensedAccounts.length}
      </TableCell>

      <TableCell>
        <Progress
          value={(agencyLicensedAccounts.length / agency_license_count) * 100}
          className="w-16"
        />
      </TableCell>

      <TableCell className="w-32">
        {isEditing ? (
          <SeatCountSpinner
            seatCount={seatCount}
            setSeatCount={setSeatCount}
            agencyLicenseCount={agency_license_count}
          />
        ) : (
          <span
            className={clsx(
              "text-button-xs",
              hasOutreachLicenses ? "text-black-300" : "text-black-500",
            )}
          >
            {agency_license_count}
          </span>
        )}
      </TableCell>

      <TableCell>{actionComponent}</TableCell>
    </TableRow>
  );
}

export default function DesktopTable({
  workspaces,
  editingWorkspaceId,
  setEditingWorkspaceId,
}: {
  workspaces: Workspace[];
  editingWorkspaceId: string;
  setEditingWorkspaceId: (id: string) => void;
}) {
  return (
    <Table>
      <thead>
        <tr>
          <TableHead>Name</TableHead>
          <TableHead>Accounts</TableHead>
          <TableHead />
          <TableHead>Seats</TableHead>
          <TableHead className="w-28" />
        </tr>
      </thead>

      <tbody>
        {workspaces.map((workspace) => {
          const { id } = workspace;
          const isEditing = editingWorkspaceId === id;

          return (
            <WorkspacesTableRow
              key={id}
              workspace={workspace}
              isEditing={isEditing}
              toggleIsEditing={() => {
                setEditingWorkspaceId(isEditing ? null : id);
              }}
            />
          );
        })}
      </tbody>
    </Table>
  );
}
